// Only instantiate once per Vue instance
import {format, formatPhoneNumber} from "../helpers/formatters.js";
import {useToast} from "vue-toastification";

const toast = useToast();

export function useInputFormatter(type, options = {}) {
  const formatType = type;
  const formatOptions = options;

  function enforceFormat(e, previousLength = 0) {
    if (!formatType) return;

    if (
      formatType === "phone" ||
      formatType === "number" ||
      formatType === "usd"
    ) {
      if (!isNumericInput(e) && !isModifierKey(e)) e.preventDefault();
    } else if (formatType === "maxLength") {
      if (willBeTooLong(e, previousLength, formatOptions.maxLength) && !isModifierKey(e)) {
        e.preventDefault();
        toast.warning("Maximum length reached");
      }
    } else if (formatType === "hex") {
      if (!isHexCharacter(e) && !isModifierKey(e)) e.preventDefault();
    } else if (formatType === "username") {
      if (!isUsernameCharacter(e) && !isModifierKey(e)) e.preventDefault();
    } else if (formatType === "website") {
      if (e.keyCode === 32) e.preventDefault();
    } else if (formatType === "handle") {
      if (e.keyCode === 32 || e.keyCode === 50) e.preventDefault();
      if (e.target.value.length === 0 && e.keyCode === 191) e.preventDefault();
    } else if (formatType === "integer") {
      if (!isNumericInput(e) && !isModifierKey(e)) e.preventDefault();
      if (e.keyCode === 190) e.preventDefault();
    } else if (type === "alphanumericdash") {
      if (!/^[A-Za-z0-9\- ]+$/.test(e.key) && !isModifierKey(e)) e.preventDefault();
    }
  }

  function applyFormat(dirty, isFocused) {
    let hasCents, centsAmountGreaterThanZero;

    let cleaned = clean(dirty);

    if (formatOptions.prefixes && formatOptions.prefixes.length > 0) cleaned = removePrefix(cleaned, formatOptions.prefixes);

    switch (formatType) {
    case "phone":
      return formatPhoneNumber(cleaned, true);
    case "usd":
      hasCents = cleaned && cleaned.toString().includes(".");
      centsAmountGreaterThanZero = hasCents && parseInt(cleaned.toString().split(".")[1]) > 0;

      if (centsAmountGreaterThanZero && !isFocused) return format(cleaned, "usd-force-cents", true);
      return format(cleaned, "usd", true);
    case "hex":
      return format(cleaned, "hex", true);
    case "username":
      return format(cleaned, "username", true);
    case "integer":
      return format(cleaned, "integer", true);
    case "":
      return formatMaxLength(cleaned, formatOptions.maxLength);
    default:
      return cleaned;
    }
  }

  function clean(dirty) {
    let numberString, number; // No variable declarations in a switch statement

    if (!dirty) dirty = "";
    let val = dirty.toString();

    if (formatOptions.prefixes && formatOptions.prefixes.length > 0) val = applyPrefixes(val, formatOptions.getPrefix(), formatOptions.prefixes);

    switch (formatType) {
    case "phone":
      if (!val) return null;
      return parseInt(val.replace(/\D/g, ""));
    case "usd":
    case "number":
    case "percentage":
      if (!val) {
        return null;
      }
      numberString = val.replace(
        formatOptions?.allowNegative ? /[^\d.-]/g : formatOptions.allowDecimal ? /[^\d.]/g : /[^\d-]/g,
        ""
      );
      number = 0;
      if (options.maximumFractionDigits === 0) {
        number = parseInt(numberString);
      } else {
        number = parseFloat(numberString);
      }
      return isNaN(number) ? null : number;
    case "username":
      return val.replace(/@/g, "");
    case "integer":
      numberString = val.replace(/\D/g, "");
      number = parseInt(numberString || "0");
      return isNaN(number) ? null : number;
    default:
      return val;
    }
  }

  return {
    enforceFormat,
    applyFormat,
    clean,
  };
}

const formatMaxLength = (string = "", maxLength) => {
  if (string.length > maxLength) {
    string = string.substring(0, maxLength);
  }
  return string;
};

const isNumericInput = (event) => {
  const key = event.keyCode;
  return (
    (key >= 48 && key <= 57) || // Allow number line
    (key >= 96 && key <= 105) || // Allow number pad
    key === 189 ||
    key === 190 || // Dash, period
    key === 110 // Decimal point on number pad
  );
};

let showTooLongWarning = true;
const willBeTooLong = (e, previousLength, maxLength) => {
  if (["Backspace", "Delete"].includes(e.key)) return false;
  let isTooLong;

  if (e.type === "paste") isTooLong = (previousLength + e.clipboardData.getData("text").length) > maxLength;
  else isTooLong = (previousLength + 1) > maxLength;

  if (isTooLong) {
    if (showTooLongWarning) {
      toast.warning((e.type === "paste" ? "Pasted content will exceed" : "Reached") + " the maximum allowed length of " + maxLength + " characters");
      showTooLongWarning = false;
      setTimeout(() => showTooLongWarning = true, 1200);
    }
  }
  return isTooLong;
};

const isUsernameCharacter = (event) => {
  const key = event.keyCode;

  if (
    (key >= 65 && key <= 90) || // A-Z
    (key >= 97 && key <= 122) || // a-z
    (key >= 48 && key <= 57) || // 0-9
    (key === 190) // period (.)
  ) {
    if (event.target.value.length !== 0) {
      // Double period (..) not allowed
      const lastChar = event.target.value[event.target.value.length - 1];
      if (key === 190 && lastChar === ".") return false;
    } else if (key === 190) return false; // The first character cannot be period (.)

    return (event.target.value.length < 30); // Max length of 30
  }

  return false;
};

const applyPrefixes = (dirty, prefix, prefixes) => {
  while (prefixes.some((prefix) => dirty.startsWith(prefix))) {
    prefixes.forEach((prefix) => {
      if (dirty.startsWith(prefix)) dirty = dirty.slice(prefix.length);
    });
  }

  if (!dirty) return "";
  if (dirty.startsWith(prefix)) return dirty;
  return prefix + dirty;
};

const removePrefix = (cleaned, prefixes) => {
  while (prefixes.some((prefix) => cleaned.startsWith(prefix))) {
    prefixes.forEach((prefix) => {
      if (cleaned.startsWith(prefix)) cleaned = cleaned.slice(prefix.length);
    });
  }

  return cleaned;
};

const isModifierKey = (event) => {
  const key = event.keyCode;
  return (
    event.shiftKey === true ||
    key === 35 ||
    key === 36 || // Allow Shift, Home, End
    key === 8 ||
    key === 9 ||
    key === 13 ||
    key === 46 || // Allow Backspace, Tab, Enter, Delete
    (key > 36 && key < 41) || // Allow left, up, right, down
    // Allow Ctrl/Command + A,C,V,X,Z
    ((event.ctrlKey === true || event.metaKey === true) &&
      (key === 65 || key === 67 || key === 86 || key === 88 || key === 90))
  );
};

const isHexCharacter = (event) => {
  return (
    event.keyCode === 51 || // Allow #
    (event.keyCode >= 48 && event.keyCode <= 57) || // 0-9
    (event.keyCode >= 65 && event.keyCode <= 70) || // A-F
    (event.keyCode >= 97 && event.keyCode <= 102) // a-f
  );
};
